import type { VoidFunctionComponent } from 'react';

import { useParams } from '../../../../../contexts/RouterContext';
import { useCurrentProject } from '../../../../../hooks/api/projects/useCurrentProject';
import { nuc } from '../../../../../utils';
import ButtonGroup from '../../../../generic/Button/ButtonGroup';
import NavigationButton from '../../../../generic/Button/NavigationButton';
import { icons } from '../../../../generic/Icon/icons';
import { DOCS_URL } from '../../../../generic/Link/index';
import Loader from '../../../../generic/Loader';
import BuildList from '../../../BuildList';
import type { BuildWithRevision } from '../../../../../types';
import type { RevisionRouteParams } from '../../../projectDashboardTypes';

type Props = {
  builds: BuildWithRevision[];
  isLoading: boolean;
  onLatestComplete: () => void;
};

/**
 * Renders the build stepper card(s) for the current revision while it is still
 * building, reusing BuildList -> ProjectBuildItem. For Provided Builds (CLI)
 * projects with no builds yet, shows the same empty-state prompt as V1.
 */
export const RevisionBuilding: VoidFunctionComponent<Props> = ({ builds, isLoading, onLatestComplete }) => {
  const params = useParams<RevisionRouteParams>();
  const { data: project, isLoading: isProjectLoading } = useCurrentProject();

  /*
   * Wait for the project and the build list to load before rendering anything,
   * otherwise the CLI empty-state copy flashes before the build cards arrive.
   */
  if (isLoading || isProjectLoading) {
    return <Loader />;
  }

  /*
   * For GitHub App hybrid projects (imported using the CLI but also connected to
   * GitHub via quick import), we allow builds to be fetched from GitHub. Instead
   * of displaying the typical CLI build card, we show the same UI used for
   * automated build cards.
   */
  const isGithubAppProject = project?.vcs_host === 'github-app';
  const isCLIBuildProject = !!project?.require_mediated_dependencies && !isGithubAppProject;

  return (
    <BuildList builds={builds} onLatestComplete={onLatestComplete}>
      <div>
        {isCLIBuildProject && (
          <div className="card">
            <h4>Provided Builds</h4>
            <p>
              This project is configured with <b>Provided Builds</b>. FOSSA has not received build results for
              this version yet. Check the status of your build and the FOSSA plugin.
            </p>
            <ButtonGroup>
              <NavigationButton
                to={`/projects/${nuc(params.project_id)}/settings/builds`}
                icon={icons.customize}
                type="secondary"
              >
                Configure Provided Builds
              </NavigationButton>
              <NavigationButton
                to={`${DOCS_URL}/provided-builds`}
                icon={icons.download}
                type="secondary"
                openInNewTab
              >
                Download the Plugin
              </NavigationButton>
            </ButtonGroup>
          </div>
        )}
      </div>
    </BuildList>
  );
};
